/**
 * Growly Oy
 * Product Delivery & Payment Boxes
 * Version: 3.1 - LOCAL DATA (No API calls)
 *
 * Changes in 3.1:
 * - Removed duplicate translation logic - delegates to CustomInventoryAPI (DRY principle)
 *
 * Changes in 3.0:
 * - Removed all API calls - reads inventory from Liquid-rendered JSON
 * - Uses ownStock/externalStock from pdpb-variant-delivery-data JSON
 * - Much faster - no network requests needed
 *
 * Changes in 2.4:
 * - PERFORMANCE: Replaced setInterval polling with MutationObserver
 * - Added change event listener as fallback
 *
 * Changes in 2.3:
 * - Added bundle product support
 */

(function() {
  'use strict';

  const container = document.querySelector('.pdpb-container');
  if (!container) return;

  // Hae varianttien toimitusaikadata (sisältää nyt ownStock ja externalStock)
  let deliveryData = null;
  const dataScript = document.getElementById('pdpb-variant-delivery-data');
  if (dataScript) {
    try {
      deliveryData = JSON.parse(dataScript.textContent);
    } catch (e) {
      console.error('PDPB: Failed to parse delivery data', e);
    }
  }

  /**
   * Translate delivery time to current locale
   * Delegates to CustomInventoryAPI if available (loaded by custom-inventory-local.js)
   */
  function translateDeliveryTime(deliveryTime) {
    if (!deliveryTime) return '';

    // Delegate to CustomInventoryAPI if available
    if (window.CustomInventoryAPI?.translateDeliveryTime) {
      return window.CustomInventoryAPI.translateDeliveryTime(deliveryTime);
    }

    // Fallback: return as-is if API not loaded
    return deliveryTime;
  }

  /**
   * Palauttaa toimitusajan ja tilan variantille
   * Lukee varastotiedot suoraan Liquid-renderöidystä JSON:sta (ei API-kutsuja)
   *
   * Bundle-tuotteet: Käyttää Shopifyn natiivia saatavuutta (variant.available)
   * koska bundleilla ei ole oikeaa varastosaldoa - ne koostuvat komponenteista
   */
  function getDeliveryInfoForVariant(variantId) {
    const defaultInfo = {
      deliveryTime: deliveryData?.defaultDeliveryTime || '2-5 arkipäivää',
      status: 'available'
    };

    if (!deliveryData || !deliveryData.variants) {
      return defaultInfo;
    }

    const variant = deliveryData.variants[variantId];
    if (!variant) {
      return {
        deliveryTime: deliveryData.productDeliveryTime || deliveryData.defaultDeliveryTime,
        status: 'available'
      };
    }

    // Ei saatavilla ollenkaan
    if (!variant.available) {
      return {
        deliveryTime: deliveryData.unavailableText,
        status: 'unavailable'
      };
    }

    // Bundle products: käytä Shopifyn natiivia saatavuutta
    if (deliveryData.isBundle) {
      return {
        deliveryTime: deliveryData.defaultDeliveryTime,
        status: 'available'
      };
    }

    // Normaalit tuotteet: lue varastosaldot JSON:sta (renderöity Liquidilla)
    const ownStock = variant.ownStock || 0;
    const externalStock = variant.externalStock || 0;

    if (ownStock > 0) {
      // Keskusvarastossa ON saldoa → oletus toimitusaika
      return {
        deliveryTime: deliveryData.defaultDeliveryTime,
        status: 'available'
      };
    }

    if (externalStock > 0) {
      // Vain tilaustuotevarastossa → metakentän arvo KÄÄNNETÄÄN
      return {
        deliveryTime: variant.deliveryTime ? translateDeliveryTime(variant.deliveryTime) : deliveryData.outOfStockText,
        status: 'backorder'
      };
    }

    // Ei kummassakaan - MUTTA tarkista Liquidin data fallbackina
    // Jos Shopify sanoo available=true, luota siihen (esim. policy=continue)
    if (variant.available && (variant.inventoryQuantity > 0 || variant.inventoryPolicy === 'continue' || variant.inventoryManagement === '')) {
      return {
        deliveryTime: deliveryData.defaultDeliveryTime,
        status: 'available'
      };
    }

    return {
      deliveryTime: deliveryData.unavailableText,
      status: 'unavailable'
    };
  }

  // ==========================================================================
  // MODAL HANDLING
  // ==========================================================================

  const deliveryTemplate = document.getElementById('pdpb-modal-delivery-template');
  const paymentTemplate = document.getElementById('pdpb-modal-payment-template');

  if (deliveryTemplate) {
    document.body.appendChild(deliveryTemplate.content.cloneNode(true));
    deliveryTemplate.remove();
  }

  if (paymentTemplate) {
    document.body.appendChild(paymentTemplate.content.cloneNode(true));
    paymentTemplate.remove();
  }

  const triggers = container.querySelectorAll('[data-pdpb-trigger]');

  function openModal(type) {
    const modal = document.querySelector(`[data-pdpb-modal="${type}"]`);
    if (modal) {
      modal.classList.add('is-open');
      document.body.style.overflow = 'hidden';
    }
  }

  function closeModal(modal) {
    modal.classList.remove('is-open');
    document.body.style.overflow = '';
  }

  triggers.forEach(trigger => {
    trigger.addEventListener('click', () => openModal(trigger.dataset.pdpbTrigger));
  });

  document.addEventListener('click', (e) => {
    const closeBtn = e.target.closest('[data-pdpb-close]');
    if (closeBtn) {
      const modal = closeBtn.closest('[data-pdpb-modal]');
      if (modal) closeModal(modal);
    }

    const overlay = e.target.closest('[data-pdpb-modal]');
    if (overlay && e.target === overlay) closeModal(overlay);
  });

  document.addEventListener('keydown', (e) => {
    if (e.key === 'Escape') {
      document.querySelectorAll('[data-pdpb-modal]').forEach(closeModal);
    }
  });

  // ==========================================================================
  // UI UPDATES
  // ==========================================================================

  function updateStatusColors(status) {
    const dot = container.querySelector('[data-pdpb-dot]');
    if (dot) {
      dot.className = `pdpb-dot pdpb-dot--${status}`;
    }

    const badge = document.querySelector('[data-pdpb-badge]');
    if (badge) {
      badge.className = `pdpb-delivery-time-badge pdpb-delivery-time-badge--${status}`;
    }

    const badgeText = document.querySelector('[data-pdpb-badge-text]');
    const modalTime = document.querySelector('.pdpb-modal-delivery-time');
    if (badgeText && modalTime) {
      const translations = deliveryData?.translations || {};
      const estimatedLabel = translations.estimatedDelivery || 'Arvioitu toimitusaika:';
      const backorderLabel = translations.backorderLabel || 'Tilaustuote:';

      if (status === 'unavailable') {
        badgeText.innerHTML = `<strong class="pdpb-modal-delivery-time">${deliveryData?.unavailableText || 'Ei saatavilla'}</strong>`;
      } else if (status === 'backorder') {
        badgeText.innerHTML = `${backorderLabel} <strong class="pdpb-modal-delivery-time">${modalTime.textContent}</strong>`;
      } else {
        badgeText.innerHTML = `${estimatedLabel} <strong class="pdpb-modal-delivery-time">${modalTime.textContent}</strong>`;
      }
    }

    container.dataset.deliveryStatus = status;
  }

  let currentVariantId = container.dataset.variantId;

  function updateDeliveryTime(variantId, force = false) {
    variantId = String(variantId);

    if (!force && variantId === currentVariantId) return;

    const info = getDeliveryInfoForVariant(variantId);

    const timeText = container.querySelector('.pdpb-delivery-time-text');
    if (timeText) timeText.textContent = info.deliveryTime;

    const modalTime = document.querySelector('.pdpb-modal-delivery-time');
    if (modalTime) modalTime.textContent = info.deliveryTime;

    updateStatusColors(info.status);

    container.dataset.variantId = variantId;
    currentVariantId = variantId;
  }

  // ==========================================================================
  // INITIALIZATION
  // ==========================================================================

  // Päivitä heti oikeilla tiedoilla
  const initialVariantId = container.dataset.variantId;
  if (initialVariantId) {
    updateDeliveryTime(initialVariantId, true);
  }

  // Käytä MutationObserveria hidden inputin seurantaan
  const hiddenInput = document.querySelector('form[action*="/cart/add"] input[name="id"], input[name="id"][type="hidden"]');

  if (hiddenInput) {
    const observer = new MutationObserver((mutations) => {
      for (const mutation of mutations) {
        if (mutation.type === 'attributes' && mutation.attributeName === 'value') {
          const newValue = hiddenInput.value;
          if (newValue && newValue !== currentVariantId) {
            updateDeliveryTime(newValue);
          }
        }
      }
    });

    observer.observe(hiddenInput, {
      attributes: true,
      attributeFilter: ['value']
    });

    // Fallback: kuuntele myös change-eventtejä
    hiddenInput.addEventListener('change', () => {
      const newValue = hiddenInput.value;
      if (newValue && newValue !== currentVariantId) {
        updateDeliveryTime(newValue);
      }
    });
  }

  // URL parametrin muutos (back/forward)
  window.addEventListener('popstate', () => {
    const variantParam = new URLSearchParams(window.location.search).get('variant');
    if (variantParam) updateDeliveryTime(variantParam);
  });

})();
